"use client";

import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { sharedChatPayload } from "@/lib/ai-chat-subject";
import { postJson } from "@/lib/fetcher";

/**
 * Share on a chat about a record: saves a copy of the thread a teammate can
 * continue (the share route seeds it from the page's own saved thread) and
 * puts its link on the clipboard. A subject without a `kind` is not about a
 * record and has nothing to share.
 *
 * @param {{ subject: import("@/lib/ai-chat-subject").ChatSubject, summaryChips?: Array, messages: Array }} chat
 * @returns {{ share: () => Promise<void>, isSharing: boolean }}
 */
export function useShareChat({ subject, summaryChips, messages }) {
  const { toast } = useToast();
  const [isSharing, setIsSharing] = useState(false);

  const share = async () => {
    if (!subject?.kind || isSharing) return;
    setIsSharing(true);
    try {
      const payload = await postJson(
        "/api/chats/share",
        sharedChatPayload(subject, {
          summaryChips,
          messages,
          contextPath: `${window.location.pathname}${window.location.search || ""}`,
        }),
      );
      if (!payload?.chatId) throw new Error("Unable to create a shared chat.");
      if (!navigator?.clipboard) {
        throw new Error("Clipboard not available in this context.");
      }
      await navigator.clipboard.writeText(
        `${window.location.origin}/chats/${payload.chatId}`,
      );
      toast({
        title: "Chat link copied",
        description: "Share the link to continue this conversation together.",
      });
    } catch (error) {
      toast({
        title: "Unable to share chat",
        description:
          error?.message || "We couldn't create a shared chat right now.",
        variant: "destructive",
      });
    } finally {
      setIsSharing(false);
    }
  };

  return { share, isSharing };
}
